import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { TUserAuthTokenPayload, useAuthenticated } from '@qapline/api/auth';
import { TInvoiceStatus } from '@qapline/core';
import { useChange, useUpdateEffect } from '@qapline/helpers';
import { timestamp } from '@qapline/helpers';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import classNames from 'classnames';
import { config } from 'config';
import { format } from 'date-fns';
import { addDays } from 'date-fns/esm';
import { Currency } from 'modules/common';
import { CustomerDetailSchema } from 'modules/customers';
import {
    CommissionTableFormArray,
    DateField,
    Form,
    FormGrid,
    FormSection,
    RenderArgs,
    SelectField,
    TextField,
    VisualFormInputsContext,
} from 'modules/form';
import {
    InvoiceFormCommissionSchema,
    InvoiceFormSchema,
    invoiceFormSchema,
    useGetCommissionsForInvoicingQuery,
    useGetInvoicingStatusesQuery,
} from 'modules/invoicing';
import { getInvoiceOrderLanguageOptions } from 'modules/invoicing/utils/orderLanguage';
import { MultiSwitch } from 'modules/multiswitch';
import { formatPrice } from 'modules/table';
import { Button, Typography } from 'modules/ui';
import { Modal } from 'modules/ui/components/Modal/Modal';
import { getInvoiceOrderDate, getInvoicePointDate } from 'utils';
import { handleErrorsWithNoInputs } from 'utils/handleErrorsWithNoInputs';

import { Autocomplete } from '../Autocomplete';
import { IInvoiceCommissionTablePopupProps, InvoiceCommissionTablePopup } from '../InvoiceCommissionTablePopup';
import { AttachmentsSection } from './components/AttachmentsSection';
import { InvoiceStates } from './components/InvoiceStates';

import styles from './InvoiceBasicForm.module.scss';

export const InvoiceBasicForm: React.FC<{
    fetchedData?: InvoiceFormSchema;
    setIsSomeValueChanged?: (isSomeValueChanged: boolean) => void;
    customer?: CustomerDetailSchema | null;
    prepareData: (
        data: InvoiceFormSchema,
        editedBy: TUserAuthTokenPayload,
        isAlreadyCreated: boolean,
        oldData?: InvoiceFormSchema,
    ) => void;
    fetchCustomer?: (customer_id: number) => void;
    invoiceId?: number;
    invoiceStatus?: TInvoiceStatus;
    /** Called with the current values whenever the form changes, so the page can keep a live PDF preview in sync. */
    onValuesChange?: (values: InvoiceFormSchema) => void;
}> = ({
    fetchedData,
    setIsSomeValueChanged,
    customer,
    prepareData,
    fetchCustomer,
    invoiceId,
    invoiceStatus,
    onValuesChange,
}) => {
    const { user } = useAuthenticated();
    const [totalPrice, setTotalPrice] = useState(0);
    const formSubmitBtnRef = useRef<HTMLButtonElement | null>(null);
    const data = useMemo(() => transformIncomingData(fetchedData), [fetchedData]);
    const [invoicingStatusesIncludeAllCommissions, setInvoicingStatusesIncludeAllCommissions] =
        useState<boolean>(false);

    const [availableCommissions, setAvailableCommissions] = useState<
        IInvoiceCommissionTablePopupProps['availableCommissions']
    >([]);
    const [showCommissionsPreview, setShowCommissionsPreview] = useState<boolean>(false);
    const [visualInputsList, setVisualInputsList] = useState<string[]>([]);
    const [pendingSubmitData, setPendingSubmitData] = useState<InvoiceFormSchema | null>(null);

    const isAlreadyCreated = Boolean(fetchedData);

    const formDefaultValues: InvoiceFormSchema = {
        paid: false,
        paidAmount: null,
        paymentConfirmationDate: null,
        paidStateChangedBy: null,
        paidStateChangedBy_id: null,
        exported: false,
        number: 'xxxxxxxx',
        customer_company: '',
        currency: null,
        dateIssue: '',
        dateOrder: '',
        datePoint: '',
        dateDue: '',
        constantSymbol: '',
        language: 'cs',
        commissions: [],
        invoiceAttachmentId: null,
        deleted: false,
        customer_id: null,
        companyRegistrationNumber: null,
    };

    const { t } = useTranslation();
    const methods = useForm<InvoiceFormSchema>({
        defaultValues: data || formDefaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: zodResolver(invoiceFormSchema(t)),
    });
    const { handleSubmit, control, reset, setValue, formState, getValues } = methods;
    const watchedValues = useWatch({ control });
    const { currency, customer_id, commissions, dateIssue, customer_company } = watchedValues;

    // getValues() rather than the watched partial: the preview wants the whole invoice, including
    // the fields nothing on this form re-renders on.
    useEffect(() => {
        onValuesChange?.(getValues());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedValues, onValuesChange]);

    const { currentData: invoicingStatusesData } = useGetInvoicingStatusesQuery(
        { includeAllCommissions: invoicingStatusesIncludeAllCommissions },
        { skip: isAlreadyCreated },
    );
    const { currentData: commissionsForInvoicingData } = useGetCommissionsForInvoicingQuery(
        {
            currency: currency || undefined,
            customerId: customer_id ?? undefined,
            includeAllCommissions: invoicingStatusesIncludeAllCommissions,
        },
        { skip: !customer_id || !currency },
    );

    const customerCompanyOptions = useMemo((): { value: number; label: string; customerCompany: string }[] => {
        if (!invoicingStatusesData) return [];
        return invoicingStatusesData.map(({ customerCompany, customerId, czkCount, eurCount }) => ({
            label: `${customerCompany} (${czkCount + eurCount})`,
            customerCompany,
            value: customerId,
        }));
    }, [invoicingStatusesData]);

    useEffect(
        () => setIsSomeValueChanged && setIsSomeValueChanged(Object.keys(formState.dirtyFields).length !== 0),
        [JSON.stringify(formState.dirtyFields), setIsSomeValueChanged],
    );

    // prefill the issue date on a new invoice once customer is loaded, so dueDate can be derived
    useEffect(() => {
        if (isAlreadyCreated || !customer || dateIssue) return;
        setValue('dateIssue', String(Math.floor(new Date().setHours(0, 0, 0, 0) / 1000)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, dateIssue]);

    // derive dueDate when issueDate changes
    useChange(
        () => {
            if (!customer?.defaultDueDate || !dateIssue) return;
            const dueDate = addDays(Number(dateIssue) * 1000, customer.defaultDueDate);
            setValue('dateDue', String(Math.floor(dueDate.getTime() / 1000)));
        },
        [customer?.defaultDueDate, dateIssue],
        [customer?.defaultDueDate],
    );

    useEffect(() => {
        if (!customer_id || !fetchCustomer || !commissionsForInvoicingData || !commissionsForInvoicingData.length)
            return;
        const firstCustomerId = commissionsForInvoicingData[0].customer?.customer_id;
        if (firstCustomerId) fetchCustomer(firstCustomerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer_id, commissionsForInvoicingData]);

    const commissionsFieldArray = useFieldArray({
        control,
        name: 'commissions',
    });

    // when currency changes, remove all commissions
    const [currCurrency, setCurrCurrency] = useState<Currency>(currency || Currency.CZK);
    useEffect(() => {
        if (currency && currency !== currCurrency) {
            setCurrCurrency(currency);
            commissionsFieldArray.remove();
            data?.commissions.forEach((item, index) => {
                setValue(`commissions.${index}.commission_id`, item.commission_id);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    const currencyOptions = useMemo((): { label: string; value: string }[] => {
        if (!customer_id || !invoicingStatusesData || !invoicingStatusesData.length) return [];
        const selectedInvoicingStatus = invoicingStatusesData.find((item) => item.customerId === customer_id);
        if (!selectedInvoicingStatus) return [];

        const options: { label: string; value: string }[] = [];
        if (selectedInvoicingStatus.czkCount) {
            options.push({
                label: `${t('common.currency.CZK')} (${selectedInvoicingStatus.czkCount})`,
                value: 'CZK',
            });
        }
        if (selectedInvoicingStatus.eurCount) {
            options.push({
                label: `${t('common.currency.EUR')} (${selectedInvoicingStatus.eurCount})`,
                value: 'EUR',
            });
        }
        return options;
    }, [customer_id, invoicingStatusesData, t]);

    useEffect(() => {
        if (!commissionsForInvoicingData || !customer_id) {
            setAvailableCommissions([]);
            return;
        }
        const transformedDataCommissions: IInvoiceCommissionTablePopupProps['availableCommissions'] = (
            data?.commissions || []
        ).map(({ pickupDate, pointDate, ...rest }) => ({
            pickupDate: Number(pickupDate),
            pointDate: Number(pointDate),
            ...rest,
        }));
        // Dates travel as unix timestamps in SECONDS, in the form and on the wire.
        const toSeconds = (v: string | number | null | undefined): number =>
            v == null || v === '' ? 0 : (timestamp(v)?.unix() ?? 0);

        const transformedAdditionalCommissions: IInvoiceCommissionTablePopupProps['availableCommissions'] =
            commissionsForInvoicingData.map(
                ({
                    commission_id,
                    qid,
                    customer,
                    customerPrice,
                    customerCurrency,
                    orderDate,
                    vat,
                    loadings,
                    discharges,
                }) => {
                    const firstLoading = loadings.reduce((prev, cur) => {
                        if (timestamp(cur.dateFrom ?? 0)?.isBefore(timestamp(prev.dateFrom ?? 0))) return cur;
                        return prev;
                    }, loadings[0]);
                    const lastDischarge = discharges.reduce((prev, cur) => {
                        if (timestamp(cur.dateFrom ?? 0)?.isAfter(timestamp(prev.dateFrom ?? 0))) return cur;
                        return prev;
                    }, discharges[0]);

                    return {
                        commission_id,
                        commissionNumber: qid ?? undefined,
                        customer_company: customer?.name ?? '',
                        pickupDate: toSeconds(firstLoading?.dateFrom),
                        pointDate: toSeconds(lastDischarge?.dateFrom),
                        loading_city_string: firstLoading?.location?.city ?? '',
                        discharge_city_string: lastDischarge?.location?.city ?? '',
                        priceCustomer: Number(customerPrice ?? 0),
                        currencyCustomer: (customerCurrency ?? Currency.CZK) as Currency,
                        loading_date: loadings.map((item) => toSeconds(item.dateFrom)),
                        discharge_date: discharges.map((item) => toSeconds(item.dateFrom)),
                        orderDate: orderDate ? String(toSeconds(orderDate)) : '0',
                        qid: qid ?? undefined,
                        vat: vat != null ? String(vat) : '21',
                    };
                },
            );

        setAvailableCommissions(transformedAdditionalCommissions.concat(transformedDataCommissions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency, customer_id, commissionsForInvoicingData]);

    // Initialize commission array fields
    useEffect(() => {
        data?.commissions.forEach((item, index) => {
            setValue(`commissions.${index}.commission_id`, item.commission_id);
            setValue(`commissions.${index}.loading_date`, item.loading_date);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // total price update
    useUpdateEffect(() => {
        if (!commissions) return;
        const newTotalPrice = commissions.reduce((acc, commission) => {
            const price = Number(commission.priceCustomer);
            const vat = commission.vat;
            return acc + (vat === '21' ? price * 1.21 : price);
        }, 0);
        setTotalPrice(newTotalPrice);
    }, [commissions]);

    const submitData = (currentData: InvoiceFormSchema) => {
        if (!user) return;
        prepareData(currentData, user, isAlreadyCreated, data);
    };

    const onSubmit = (currentData: InvoiceFormSchema) => {
        if (!user) return;
        if (currentData.commissions.length < 1) return toast.error(t('invoicing.emptyCommissions'));
        if (Number(currentData.dateIssue) - Number(currentData.datePoint) > 14 * 24 * 60 * 60)
            return toast.error(t('invoicing.maxIssueDate'));
        if (Number(currentData.datePoint) > Math.floor(new Date().setHours(0, 0, 0, 0) / 1000))
            return toast.error(t('invoicing.pointDateMustBeToday'));

        // Confirm before un-marking a previously paid invoice — it may re-trigger due-date reminder emails
        if (data?.paid && !currentData.paid) {
            setPendingSubmitData(currentData);
            return;
        }

        submitData(currentData);
    };

    return (
        <FormProvider {...methods}>
            <VisualFormInputsContext.Provider
                value={{
                    visualInputsList,
                    setVisualInputsList,
                }}
            >
                {showCommissionsPreview && (
                    <InvoiceCommissionTablePopup
                        checked={commissions?.map((c) => c.commission_id as number) || []}
                        availableCommissions={availableCommissions}
                        onCancel={() => setShowCommissionsPreview(false)}
                        onApprove={(newCommissionsList) => {
                            commissionsFieldArray.replace(newCommissionsList);
                            setValue('commissions', newCommissionsList);

                            const newOrderDate = getInvoiceOrderDate(newCommissionsList);
                            newOrderDate && setValue('dateOrder', newOrderDate);

                            const newPointDate = getInvoicePointDate(newCommissionsList);
                            newPointDate && setValue('datePoint', newPointDate);
                        }}
                    />
                )}
                <Form
                    id="invoice-form"
                    onSubmit={handleSubmit(onSubmit, (error) => handleErrorsWithNoInputs(error, visualInputsList))}
                    onReset={() => reset()}
                >
                    <button ref={formSubmitBtnRef} type="submit" className={styles.hidden}></button>
                    <InvoiceStates invoiceId={invoiceId} status={invoiceStatus} />
                    <FormSection
                        title={t('invoicing.form.basicInfo.sectionTitle')}
                        headerEndSlot={
                            <MultiSwitch
                                name="toggle"
                                value={invoicingStatusesIncludeAllCommissions ? 'true' : 'false'}
                                size="small"
                                onChange={(value) => {
                                    setInvoicingStatusesIncludeAllCommissions(value === 'true');
                                }}
                                options={[
                                    {
                                        label: t('invoicing.form.basicInfo.readyForInvoicing'),
                                        value: 'false',
                                    },
                                    {
                                        label: t('invoicing.form.basicInfo.allCommissions'),
                                        value: 'true',
                                    },
                                ]}
                            />
                        }
                    >
                        <FormGrid columns={3}>
                            <TextField
                                name="number"
                                label={t('invoicing.form.basicInfo.invoiceNumber')}
                                disabled={true}
                            />
                            <Autocomplete
                                name="customer_id"
                                label={t('invoicing.form.basicInfo.customer_company')}
                                options={
                                    isAlreadyCreated
                                        ? [
                                              {
                                                  label: customer_company,
                                                  value: customer_id,
                                                  customerCompany: customer_company as string,
                                              },
                                          ]
                                        : customerCompanyOptions
                                }
                                labelProperty="label"
                                valueProperty="value"
                                isDisabled={isAlreadyCreated}
                                onChange={(value) => {
                                    if (value) setValue('customer_company', value.customerCompany);
                                    if (!isAlreadyCreated) {
                                        setValue('commissions', []);
                                    }
                                }}
                            />
                            <Autocomplete
                                name="currency"
                                label={t('invoicing.form.basicInfo.currency')}
                                options={
                                    isAlreadyCreated
                                        ? [{ label: `${currency}`, value: currency as string }]
                                        : currencyOptions
                                }
                                labelProperty="label"
                                valueProperty="value"
                                onChange={() => {
                                    if (!isAlreadyCreated) {
                                        setValue('commissions', []);
                                    }
                                }}
                                isDisabled={isAlreadyCreated || !customer_id}
                            />
                        </FormGrid>
                    </FormSection>
                    <CommissionTableFormArray
                        {...commissionsFieldArray}
                        name="commissions"
                        title={t('invoicing.form.commissions.sectionTitle')}
                        addTitle={t('invoicing.form.commissions.addCommission')}
                        onAddCommissionBtnClick={() => setShowCommissionsPreview((prev) => !prev)}
                        totalPriceText={`${formatPrice(Number(totalPrice))} ${t(
                            `common.currency.${currency || 'CZK'}`,
                        )}`}
                        defaultValues={{
                            orderDate: '0',
                            loading_city_string: '',
                            discharge_city_string: '',
                            vat: '21',
                            priceCustomer: 0,
                            commission_id: 0,
                            currencyCustomer: Currency['CZK'],
                            customer_company: '',
                            loading_date: [],
                            discharge_date: [],
                        }}
                        render={({
                            getFieldProps,
                            index,
                        }: RenderArgs<InvoiceFormSchema, InvoiceFormCommissionSchema>) => {
                            return (
                                <>
                                    <CommissionTableCell
                                        className={styles.CommissionTableFormLinkCell}
                                        onClick={() => {
                                            window.open(
                                                `${config.routes.commission.overview}/${commissionsFieldArray.fields[index].commission_id}`,
                                                '_blank',
                                            );
                                        }}
                                        {...getFieldProps('commissionNumber')}
                                    />
                                    <CommissionTableCell
                                        {...getFieldProps('pickupDate')}
                                        modificator={(value) =>
                                            value ? format(timestamp(value)?.toDate() ?? 0, 'd. M. yyyy') : ''
                                        }
                                    />
                                    <CommissionTableCell {...getFieldProps('loading_city_string')} />
                                    <CommissionTableCell {...getFieldProps('discharge_city_string')} />
                                    <CommissionTableCellVat {...getFieldProps('vat')} />
                                    <CommissionTableCell
                                        className={styles.CommissionTableFormLastCell}
                                        {...getFieldProps('priceCustomer')}
                                        modificator={(value) =>
                                            `${formatPrice(Number(value))} ${t(`common.currency.${currency || 'CZK'}`)}`
                                        }
                                    />
                                </>
                            );
                        }}
                    />
                    <FormSection title={t('invoicing.form.dates.sectionTitle')}>
                        <FormGrid columns={2} rows={2}>
                            <DateField name="dateIssue" label={t('invoicing.form.dates.issueDate')} />
                            <DateField name="dateOrder" label={t('invoicing.form.dates.orderDate')} />
                            <DateField name="datePoint" label={t('invoicing.form.dates.pointDate')} />
                            <DateField name="dateDue" label={t('invoicing.form.dates.dueDate')} />
                        </FormGrid>
                    </FormSection>
                    <FormSection title={t('invoicing.form.order.sectionTitle')}>
                        <FormGrid columns={2}>
                            <TextField name="constantSymbol" label={t('invoicing.form.order.constantSymbol')} />
                            <SelectField
                                name="language"
                                label={t('invoicing.form.order.language')}
                                options={getInvoiceOrderLanguageOptions(t)}
                            />
                        </FormGrid>
                    </FormSection>
                    {data?.invoice_id && <AttachmentsSection invoiceId={data.invoice_id} invoiceNumber={data.number} />}
                </Form>
                {pendingSubmitData && (
                    <Modal
                        onClick={() => setPendingSubmitData(null)}
                        label={t('invoicing.unmarkPaidConfirm.label')}
                        description={t('invoicing.unmarkPaidConfirm.description')}
                        cancelComponent={
                            <Button type="button" variant="secondary" onClick={() => setPendingSubmitData(null)}>
                                {t('invoicing.unmarkPaidConfirm.cancel')}
                            </Button>
                        }
                        approveComponent={
                            <Button
                                type="button"
                                variant="primary"
                                onClick={() => {
                                    const dataToSubmit = pendingSubmitData;
                                    setPendingSubmitData(null);
                                    submitData(dataToSubmit);
                                }}
                            >
                                {t('invoicing.unmarkPaidConfirm.approve')}
                            </Button>
                        }
                    />
                )}
            </VisualFormInputsContext.Provider>
        </FormProvider>
    );
};

function transformIncomingData(data?: InvoiceFormSchema): InvoiceFormSchema | undefined {
    if (!data) return;
    const fetchedData = structuredClone(data);

    fetchedData.commissions = fetchedData.commissions.map((item) => ({
        ...item,
        pickupDate: item.loading_date.reduce<number | null>(
            (prev, next) => (Number(prev) < Number(next) ? Number(prev) : Number(next)),
            item.loading_date[0] ? Number(item.loading_date[0]) : null,
        ),
        pointDate: item.discharge_date.reduce<number | null>(
            (prev, next) => (Number(prev) > Number(next) ? Number(prev) : Number(next)),
            null,
        ),
    }));
    fetchedData.dateOrder = fetchedData.dateOrder || getInvoiceOrderDate(fetchedData.commissions);

    return fetchedData;
}

const CommissionTableCell = ({
    name,
    modificator,
    className,
    ...props
}: {
    name: string;
    modificator?: (value: string) => string;
} & React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <Controller
            name={name}
            render={({ field }) => (
                <div className={classNames([styles.CommissionTableFormArrayCell, className])} {...props}>
                    <Typography variant="h4" fontWeight="normal" className={styles.CommissionTableFormArrayCellInner}>
                        {modificator ? modificator(field.value) : field.value}
                    </Typography>
                </div>
            )}
        />
    );
};

export const invoicingVatNumbers = ['0', '21'];

const CommissionTableCellVat = ({ name }: { name: string }) => (
    <Controller
        name={name}
        render={({ field: { value, onChange } }) => {
            const onIncrease = () => {
                if (value !== invoicingVatNumbers[invoicingVatNumbers.length - 1]) {
                    const indexOfCurrentIndex = invoicingVatNumbers.indexOf(value);
                    onChange(invoicingVatNumbers[+indexOfCurrentIndex + 1]);
                }
            };
            const onDecrease = () => {
                if (value !== invoicingVatNumbers[0]) {
                    const indexOfCurrentIndex = invoicingVatNumbers.indexOf(value);
                    onChange(invoicingVatNumbers[indexOfCurrentIndex - 1]);
                }
            };

            return (
                <div className={`${styles.CommissionTableFormArrayCell} ${styles.vatPicker}`}>
                    {value}%
                    <div className={styles.icons}>
                        <button type="button" className={styles.chevron} onClick={onIncrease}>
                            <ChevronUpIcon />
                        </button>
                        <button type="button" className={styles.chevron} onClick={onDecrease}>
                            <ChevronDownIcon />
                        </button>
                    </div>
                </div>
            );
        }}
    />
);
